import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  fetchAllowedMenus,
  fetchUserGroupData,
  fetchUserData,
  createUser,
  fetchAllUserType,
  userTypeItem,
} from "../Services/APIService";
import OverlayLoader from "../Components/OverlayLoader";
import { on } from "events";

interface ContactFormData {
  email: string;
  firstName: string;
  lastName: string;
  contactOwner: string;
  jobTitle: string;
  phoneNumber: string;
}

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => void;
  onUserDataAdd: () => void; // Callback to refresh user data after creation
}

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface UserGroup {
  groupId: number;
  groupName: string;
}

interface User {
  userId: number;
  userName: string;
  email: string;
}

interface SelectOption {
  value: number;
  label: string;
  userData: User;
}

interface ValidationErrors {
  userName?: string;
  mobile?: string;
  email?: string;
  alternativePhone?: string;
  password?: string;
  groupId?: string;
  userTypeId?: string;
  loginUserName?: string;
}

const CreateContactModal: React.FC<CreateContactModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onUserDataAdd, // Callback to refresh user data after creation
}) => {
  // create user code start
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [userTypes, setUserTypes] = useState<userTypeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUserTypes, setIsLoadingUserTypes] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    userId: "",
    userName: "",
    mobile: "",
    email: "",
    alternativePhone: "",
    reportTo: null as User | null,
    password: "",
    groupId: "",
    isFallbackUser: false,
    isActive: true,
    userTypeId: "",
    loginUserName: "",
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }

        const groups = await fetchUserGroupData();
        setUserGroups(groups);

        const usersData = (await fetchUserData()).filter(
          (user) => user.userTypeId == 2
        );
        setUsers(usersData);

        const options = usersData.map((user) => ({
          value: user.userId,
          label: `${user.userName} (${user.email})`,
          userData: user,
        }));
        setUserOptions(options);

        setIsLoadingUserTypes(true);
        const types = await fetchAllUserType();
        if (types && types.length > 0) {
          setUserTypes(types);
          setFormData((prev) => ({
            ...prev,
            userTypeId: types[0].typeId.toString(),
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
        setIsLoadingUserTypes(false);
      }
    };

    getData();
  }, []);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateMobile = (mobile: string): boolean => {
    const mobileRegex = /^\d+$/;
    return mobileRegex.test(mobile);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    // Handle checkboxes
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked,
      });
      return;
    }

    // Prevent leading spaces for all inputs
    if (value.startsWith(" ")) {
      return;
    }

    // Special validation for mobile numbers - only allow digits
    if ((name === "mobile" || name === "alternativePhone") && value !== "") {
      if (!/^\d*$/.test(value)) {
        return;
      }
    }

    // Update form data
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear errors when user types
    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  // Handle multi-select changes for reportTo
  const handleSingleSelectChange = (selectedOption: SelectOption | null) => {
    if (!selectedOption) return;

    setFormData({
      ...formData,
      reportTo: selectedOption.userData,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // Required field validations
    if (!formData.userName.trim()) {
      newErrors.userName = "User name is required";
      isValid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (!validateMobile(formData.mobile)) {
      newErrors.mobile = "Mobile number should contain only digits";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (
      formData.alternativePhone &&
      !validateMobile(formData.alternativePhone)
    ) {
      newErrors.alternativePhone =
        "Alternative phone should contain only digits";
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    if (!formData.groupId) {
      newErrors.groupId = "Group selection is required";
      isValid = false;
    }

    // User Type validation
    if (!formData.userTypeId) {
      newErrors.userTypeId = "User Type selection is required";
      isValid = false;
    }

    if (!formData.loginUserName.trim()) {
      newErrors.loginUserName = "Login user name is required";
      isValid = false;
    } else if (formData.loginUserName.includes(" ")) {
      newErrors.loginUserName = "Login user name must not contain spaces";
      isValid = false;
    }

    if (formData.loginUserName.length > 10) {
      newErrors.loginUserName = "Login user name cannot exceed 10 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    const userId = localStorage.getItem("UserId");
    const tenantId = localStorage.getItem("tenantId");

    // Ensure reportTo is not null before accessing properties
    const reportToIds = formData.reportTo
      ? formData.reportTo.userId.toString()
      : "";

    // Get selected user group name
    const selectedGroup =
      userGroups?.find(
        (group) => group.groupId?.toString() === formData.groupId
      ) || null;

    const groupName = selectedGroup ? selectedGroup.groupName : "Unknown";

    const body = {
      userName: formData.userName,
      mobile: formData.mobile,
      email: formData.email,
      alternativePhone: formData.alternativePhone,
      reportTo: parseInt(reportToIds) || 0,
      passwordHash: formData.password || "default@123",
      groupId: parseInt(formData.groupId),
      isFallbackUser: formData.isFallbackUser,
      requestedBy: userId ? parseInt(userId) : 0,
      userStatusId: 1,
      userTypeId: formData.userTypeId ? parseInt(formData.userTypeId) : 0,
      tenantId: tenantId ? parseInt(tenantId) : 0,
      loginUserName: formData.loginUserName.trim(),
    };

    try {
      console.log("Request Body:", body);
      const result = await createUser(body);

      if (result.isCreated) {
        toast.success(result.message);
        if (onUserDataAdd) {
          onUserDataAdd(); // Refresh user data after creation
        }
        setTimeout(() => {
          handleClose();
        }, 100);
      } else {
        toast.error(result.message || "User creation failed");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error occurred during user creation");
    }
  };

  // CSS for required field indicator
  const requiredField = <span className="text-danger ms-1">*</span>;

  const handleClose = () => {
    setFormData({
      userId: "",
      userName: "",
      mobile: "",
      email: "",
      alternativePhone: "",
      reportTo: null as User | null,
      password: "",
      groupId: "",
      isFallbackUser: false,
      isActive: true,
      userTypeId: "",
      loginUserName: "",
    });
    setErrors({});
    onClose();
  };
  return (
    <>
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Create User</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>
          <div className="modal-body-custom">
            <div className="modal-body-content">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="userName">
                      User Name{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-user"></span>
                      <input
                        name="userName"
                        id="userName"
                        placeholder="Enter User Name"
                        value={formData.userName}
                        onChange={handleChange}
                        required
                        className={`form-control-custom ${
                          errors.userName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.userName && (
                        <div className="invalid-feedback-custom">
                          {errors.userName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-6 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="mobile">
                      Mobile No.{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-phone"></span>
                      <input
                        name="mobile"
                        id="mobile"
                        placeholder="Enter Mobile No."
                        value={formData.mobile}
                        onChange={handleChange}
                        required
                        className={`form-control-custom ${
                          errors.mobile ? "is-invalid" : ""
                        }`}
                      />
                      {errors.mobile && (
                        <div className="invalid-feedback-custom">
                          {errors.mobile}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-lg-6 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="alternativePhone">
                      Alternative Phone
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-phone"></span>
                      <input
                        name="alternativePhone"
                        id="alternativePhone"
                        placeholder="Enter Alternative Phone"
                        value={formData.alternativePhone}
                        onChange={handleChange}
                        required
                        className={`form-control-custom ${
                          errors.alternativePhone ? "is-invalid" : ""
                        }`}
                      />
                      {errors.alternativePhone && (
                        <div className="invalid-feedback-custom">
                          {errors.alternativePhone}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="email">
                      Email{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-email"></span>
                      <input
                        name="email"
                        id="email"
                        placeholder="Enter Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`form-control-custom ${
                          errors.email ? "is-invalid" : ""
                        }`}
                      />
                      {errors.email && (
                        <div className="invalid-feedback-custom">
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label">Reports To</label>
                    <Select
                      options={userOptions}
                      value={
                        formData.reportTo
                          ? {
                              value: formData.reportTo.userId,
                              label: `${formData.reportTo.userName} (${formData.reportTo.email})`,
                              userData: formData.reportTo,
                            }
                          : null
                      }
                      onChange={handleSingleSelectChange}
                      className="basic-single-select"
                      classNamePrefix="select"
                      placeholder="Select manager"
                      isLoading={isLoading}
                    />
                  </div>

                  <div className="col-12 col-lg-6 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="password">
                      Password{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-password"></span>
                      <input
                        name="password"
                        id="password"
                        placeholder="Enter password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className={`form-control-custom ${
                          errors.password ? "is-invalid" : ""
                        }`}
                      />
                      {errors.password && (
                        <div className="invalid-feedback-custom">
                          {errors.password}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-6 mb-3 input-group-custom">
                    <label className="form-label">Group{requiredField}</label>
                    <select
                      name="groupId"
                      value={formData.groupId}
                      onChange={handleChange}
                      required
                      className={`form-select form-control-lg ${
                        errors.groupId ? "is-invalid" : ""
                      }`}
                    >
                      <option value="">Select Group</option>
                      {userGroups.map((group) => (
                        <option key={group.groupId} value={group.groupId}>
                          {group.groupName}
                        </option>
                      ))}
                    </select>
                    {errors.groupId && (
                      <div className="invalid-feedback text-danger">
                        {errors.groupId}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="loginUserName">
                      Login User Name{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-user"></span>
                      <input
                        name="loginUserName"
                        id="loginUserName"
                        placeholder="Enter Login User Name"
                        value={formData.loginUserName}
                        onChange={handleChange}
                        maxLength={10}
                        required
                        className={`form-control-custom ${
                          errors.loginUserName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.loginUserName && (
                        <div className="invalid-feedback-custom">
                          {errors.loginUserName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label">
                      User Type{requiredField}
                    </label>
                    <select
                      name="userTypeId"
                      value={formData.userTypeId}
                      onChange={handleChange}
                      required
                      className={`form-select form-control-lg ${
                        errors.userTypeId ? "is-invalid" : ""
                      }`}
                      disabled={userTypes.length === 0 || isLoadingUserTypes}
                    >
                      {isLoadingUserTypes ? (
                        <option value="">Loading user types...</option>
                      ) : userTypes.length > 0 ? (
                        userTypes.map((type) => (
                          <option key={type.typeId} value={type.typeId}>
                            {type.typeName}
                          </option>
                        ))
                      ) : (
                        <option value="">No user types available</option>
                      )}
                    </select>
                    {errors.userTypeId && (
                      <div className="invalid-feedback text-danger">
                        {errors.userTypeId}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 mt-3">
                    <div className="row">
                      <div className="col-12 col-lg-6">
                        <label className="form-check">
                          Is Fallback User
                          <input
                            type="checkbox"
                            name="isFallbackUser"
                            checked={formData.isFallbackUser}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                        </label>
                      </div>
                      <div className="col-12 col-lg-6">
                        <label className="form-check">
                          Is Active
                          <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer-custom">
              <button
                type="button"
                onClick={handleClose}
                className="btn-custom btn-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn-custom btn-primary-custom"
              >
                Create
              </button>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={isLoading}
          message="Loading User Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default CreateContactModal;
